import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from '@amzn/alchemy-components-react';
import type { UseBulkManageScan2AuthResult } from "src/hooks/use-bulk-manage-scan2auth";
import { StepFrame } from "src/components/modals/bulk-manage-credentials/step-frame";
import { expandableProps } from "src/components/modals/bulk-manage-credentials/expandable";
import {
    actionDescription,
    actionLabel,
    ineligibleLabel,
    isDestructiveAction,
    userStatusLabel,
} from "src/components/modals/bulk-manage-credentials/copy";

interface SelectActionStepProps {
    /**
     * The wizard's view model. Passed whole rather than destructured into a dozen props: every field a
     * step reads comes from the one hook instance, so handing over the result keeps the steps and the
     * hook from drifting apart on what "the current wizard state" means.
     */
    bulk: UseBulkManageScan2AuthResult
}

/**
 * Step 1: pick an action. Each action row shows how many of the operator's selected principals it
 * would actually apply to, and expands to name them — eligible ones and, in a nested collapsible list,
 * the ineligible ones with the reason. An action whose eligible cohort is empty can't be selected, so
 * the operator can't reach a confirm step that would send nothing.
 */
export const SelectActionStep = (props: SelectActionStepProps) => {
    const { t } = useTranslation();
    const {
        users,
        actions,
        expandedActions,
        expandedIneligible,
        toggleActionExpand,
        toggleIneligibleExpand,
        handleSelectAction,
        eligibilityFor,
        nowMs,
    } = props.bulk;

    return (
        <StepFrame>
            <div>
                <p className="mb-1">{t('bulk-manage-credentials-select-prompt', { count: users.length })}</p>
                <p className="mb-3 text-muted" style={{ fontSize: '0.85rem' }}>
                    {t('bulk-manage-credentials-select-overlap-note')}
                </p>
                {actions.map(action => {
                    const { eligible, ineligible } = eligibilityFor(action);
                    const isActionExpanded = expandedActions.has(action);
                    const destructive = isDestructiveAction(action);
                    return (
                        <div
                            key={action}
                            className="mb-2"
                            style={{
                                border: destructive
                                    ? '1px solid var(--color-base-error-alert, #c00)'
                                    : '1px solid #dee2e6',
                                borderRadius: '4px',
                            }}
                        >
                            {/* Title row (name + caret) and the Select button; the count sits on its own
                                line below so long action labels can't collide with the button. */}
                            <div className="p-2">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div
                                        className="d-flex align-items-center"
                                        style={{ cursor: 'pointer', minWidth: 0, flex: 1 }}
                                        {...expandableProps(isActionExpanded, () => toggleActionExpand(action))}
                                    >
                                        <Icon icon={isActionExpanded ? 'caretDown' : 'caretRight'} className="mr-1" />
                                        <strong style={destructive ? { color: 'var(--color-base-error-alert, #c00)' } : undefined}>
                                            {actionLabel(t, action)}
                                        </strong>
                                    </div>
                                    {/* Deliberately not styled destructive. This button only navigates to
                                        the confirm step, where the real commitment and the red confirm
                                        button are; the row already carries a red border and red title.
                                        It previously passed destructiveButtonStyle, which never rendered
                                        (see copy.ts) and would now be a silent no-op on variant="base". */}
                                    <Button
                                        id={`bulk-action-${action}`}
                                        className="ml-2 flex-shrink-0"
                                        size="sm"
                                        label={t('bulk-manage-credentials-select-action-button')}
                                        variant="base"
                                        disabled={eligible.length === 0}
                                        onClick={() => handleSelectAction(action)}
                                    />
                                </div>
                                <p className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                                    {actionDescription(t, action)}
                                </p>
                                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    {t('bulk-manage-credentials-eligible-count', { eligible: eligible.length, total: users.length })}
                                </span>
                            </div>
                            {isActionExpanded && (
                                <div className="px-3 pb-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                                    {eligible.map(user => (
                                        <div key={user.principalArn} className="d-flex justify-content-between py-1" style={{ fontSize: '0.85rem' }}>
                                            <span className="mr-2">
                                                <Icon icon="checkSolid" size="sm" className="mr-1 c-success-alert" />
                                                {user.fullName}
                                            </span>
                                            <span className="text-muted flex-shrink-0 text-right">{userStatusLabel(t, user, nowMs)}</span>
                                        </div>
                                    ))}
                                    {ineligible.length > 0 && (
                                        <>
                                            <div
                                                className="text-muted mt-2 mb-1 d-flex align-items-center"
                                                style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer' }}
                                                {...expandableProps(expandedIneligible.has(action), () => toggleIneligibleExpand(action))}
                                            >
                                                <Icon icon={expandedIneligible.has(action) ? 'caretDown' : 'caretRight'} className="mr-1" />
                                                {t('bulk-manage-credentials-not-eligible', { count: ineligible.length })}
                                            </div>
                                            {expandedIneligible.has(action) && ineligible.map(({ user, reason }) => (
                                                <div key={user.principalArn} className="d-flex justify-content-between py-1 text-muted" style={{ fontSize: '0.85rem' }}>
                                                    <span className="mr-2">
                                                        <Icon icon="infoSolid" size="sm" className="mr-1" />
                                                        {user.fullName}
                                                    </span>
                                                    <span className="flex-shrink-0 text-right">{ineligibleLabel(t, user, reason, nowMs)}</span>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </StepFrame>
    );
};
